import { useSearchParams } from 'react-router-dom';
import { memo } from 'react';

import { Volume } from '~/shared/types/api';
import UsageChart from '~/shared/components/UtilizationCharts/UsageChart';
import useUtilizationQueryParams from '~/shared/components/UtilizationCharts/useUtilizationQueryParams';
import ChartFilterDropdown from '~/shared/components/ChartFilterDropdown';
import {
  CUSTOM_DATES_TITLE,
  dateFilterItems,
} from '~/shared/components/UtilizationCharts/dateFilterMenuItems';
import UtilizationChart from '~/shared/components/UtilizationCharts/UtilizationChart';
import { ChartSyncProvider } from '~/shared/components/LineChart';
import Typography from '~/shared/components/Typography';

import {
  // usageDateFilterKey,
  // usageRefreshStateKey,
  usageDateFilterKey,
  usageRefreshStateKey,
  utilizationDateFilterKey,
  utilizationFullGraphKey,
  utilizationRefreshStateKey,
} from '../common/serachParamKeys';

const Utilization = memo(function Utilization({ volumeId }: { volumeId: Volume['volumeId'] }) {
  const [searchParams, setSearchParams] = useSearchParams();

  const activeRefreshState = searchParams.get(utilizationRefreshStateKey);
  const activeDateFilter = searchParams.get(utilizationDateFilterKey);
  const activeUsageRefreshState = searchParams.get(usageRefreshStateKey);
  const activeUsageDateFilter = searchParams.get(usageDateFilterKey);

  const { utilizationQueryUrl, onRefetchUtilizationQuery } = useUtilizationQueryParams({
    utilizationDateFilterSearchParam: activeDateFilter,
    showFullGraphSearchParam: searchParams.get(utilizationFullGraphKey),
    refreshStateSearchParams: activeRefreshState,
    volumeId,
  });

  const { utilizationQueryUrl: usageQueryUrl, onRefetchUtilizationQuery: onRefetchUsageQuery } =
    useUtilizationQueryParams({
      utilizationDateFilterSearchParam: activeUsageDateFilter,
      showFullGraphSearchParam: searchParams.get(utilizationFullGraphKey),
      refreshStateSearchParams: activeUsageRefreshState,
      volumeId,
    });

  // Determine selected item object (mirrors ReportsFilterSection logic)
  const activeDateFilterSelectedItem = (() => {
    if (activeDateFilter && activeDateFilter.includes(CUSTOM_DATES_TITLE)) {
      return { title: activeDateFilter };
    }
    return (
      dateFilterItems.find((i) => i.title === activeDateFilter) ||
      dateFilterItems.find((i) => i.title === 'Last 3 hours') ||
      dateFilterItems[0] || { title: 'Last 3 hours' }
    );
  })();

  return (
    <div className="col-span-2 grid grid-cols-2 gap-3.5 mt-3 pb-6">
      <div className="col-span-2 flex items-center justify-between pt-5">
        <Typography variant="H5" className="">
          Utilization over time
        </Typography>
        <ChartFilterDropdown
          align="top-right"
          items={dateFilterItems}
          onSelect={(item) => {
            setSearchParams(
              (params) => {
                params.set(utilizationDateFilterKey, item.title);
                params.set(usageDateFilterKey, item.title);
                return params;
              },
              { replace: true }
            );
          }}
          selectedItem={activeDateFilterSelectedItem as (typeof dateFilterItems)[number]}
        />
      </div>
      <ChartSyncProvider>
        <UtilizationChart
          activeDateFilter={activeDateFilter || 'Last 3 hours'}
          activeRefreshState={activeRefreshState || ''}
          queryUrlString={utilizationQueryUrl}
          onClickRefresh={onRefetchUtilizationQuery}
          onSelectRefreshState={(item) => {
            setSearchParams(
              (params) => {
                params.set(utilizationRefreshStateKey, item.title);
                return params;
              },
              { replace: true }
            );
          }}
          onSelectDateFilter={(filter) => {
            setSearchParams(
              (params) => {
                params.set(utilizationDateFilterKey, filter.title);
                params.set(usageDateFilterKey, filter.title);
                return params;
              },
              { replace: true }
            );
          }}
        />
        <UsageChart
          activeDateFilter={activeUsageDateFilter || 'Last 3 hours'}
          activeRefreshState={activeUsageRefreshState || ''}
          queryUrlString={usageQueryUrl}
          onClickRefresh={onRefetchUsageQuery}
          onSelectRefreshState={(item) => {
            setSearchParams(
              (params) => {
                params.set(usageRefreshStateKey, item.title);
                return params;
              },
              { replace: true }
            );
          }}
          onSelectDateFilter={(filter) => {
            setSearchParams(
              (params) => {
                params.set(usageDateFilterKey, filter.title);
                params.set(utilizationDateFilterKey, filter.title);
                return params;
              },
              { replace: true }
            );
          }}
        />
      </ChartSyncProvider>
    </div>
  );
});

export default Utilization;
